<script setup>
import BasketIcon from '@iconify-vue/ph/basket';
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import CoinsIcon from '@iconify-vue/ph/coins';
import EnvelopeOpenIcon from '@iconify-vue/ph/envelope-open';
import EyeIcon from '@iconify-vue/ph/eye';
import MouseLeftClickIcon from '@iconify-vue/ph/mouse-left-click';
import ProhibitIcon from '@iconify-vue/ph/prohibit';
import ShoppingCartSimpleIcon from '@iconify-vue/ph/shopping-cart-simple';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CardGrid from '@js/components/cards/CardGrid.vue';
import MetricCard from '@js/components/cards/MetricCard.vue';
import LineChart from '@js/components/charts/LineChart.vue';
import DateRangePicker from '@js/components/pickers/DateRangePicker.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PercentageCell from '@js/components/tables/cells/PercentageCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { computed, h, ref } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                props.row.original.type
                    ? h(PillCell, {
                          pillValue: props.row.original.type.label,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'isActive',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actif',
                }),
            cell: (props) =>
                props.row.original.isActive !== undefined
                    ? h(StatusCell, {
                          status: props.row.original.isActive,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'emails',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Envoyés',
                }),
            cell: (props) =>
                props.row.original.emails
                    ? h(NumberCell, {
                          number: props.row.original.emails,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'opened',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Ouverts',
                }),
            cell: (props) =>
                props.row.original.opened
                    ? h(PercentageCell, {
                          percentage: props.row.original.opened,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'clicked',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Clics',
                }),
            cell: (props) =>
                props.row.original.clicked
                    ? h(PercentageCell, {
                          percentage: props.row.original.clicked,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfOrders',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nb cdes',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfOrders,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'revenue',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA TTC',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.revenue,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'unsub',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Désabonnement',
                }),
            cell: (props) =>
                props.row.original.unsub
                    ? h(PercentageCell, {
                          percentage: props.row.original.unsub,
                          withDecimals: true,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            props.row.getCanExpand()
                                ? h(ActionButton, {
                                      size: 'small',
                                      type: 'secondary',
                                      square: true,
                                      title: props.row.getIsExpanded()
                                          ? 'Réduire'
                                          : 'Développer',
                                      callback: () =>
                                          props.row.toggleExpanded(),
                                      icon: CaretDownIcon,
                                      rotateIcon: props.row.getIsExpanded(),
                                  })
                                : null,
                    },
                ),
        }),
    ],
    url: route(
        'front.projectManagement.modules.postEmailingAutomationStatistics',
        {
            tenant: tenant.id,
        },
    ),
    columnPinning: {
        right: ['details'],
    },
});

const options = ref({
    byDay: {
        label: 'Par jour',
        active: true,
        action: () => {
            options.value.byDay.active = true;
            options.value.byWeek.active = false;
            options.value.byMonth.active = false;
        },
    },
    byWeek: {
        label: 'Par semaine',
        active: false,
        action: () => {
            options.value.byDay.active = false;
            options.value.byWeek.active = true;
            options.value.byMonth.active = false;
        },
    },
    byMonth: {
        label: 'Par mois',
        active: false,
        action: () => {
            options.value.byDay.active = false;
            options.value.byWeek.active = false;
            options.value.byMonth.active = true;
        },
    },
});

const emailsSentAndNumberOfOrdersDatasets = computed(() => {
    let datasets = [];

    let emailsSentChartData = options.value.byDay?.active
        ? globalData.value.emailsSentChartDataByDay
        : options.value.byWeek?.active
          ? globalData.value.emailsSentChartDataByWeek
          : globalData.value.emailsSentChartDataByMonth;

    datasets.push({
        group: 'Emails envoyés',
        label: 'Emails envoyés',
        backgroundColor: '#F9AB00',
        borderColor: '#F9AB00',
        pointRadius: 4,
        pointHoverRadius: 5,
        fill: false,
        data: emailsSentChartData.baseData,
        labels: emailsSentChartData.baseLabels,
        yAxisID: 'y',
    });

    let numberOfOrdersChartData = options.value.byDay?.active
        ? globalData.value.numberOfOrdersChartDataByDay
        : options.value.byWeek?.active
          ? globalData.value.numberOfOrdersChartDataByWeek
          : globalData.value.numberOfOrdersChartDataByMonth;

    datasets.push({
        group: 'Commandes',
        label: 'Commandes',
        backgroundColor: '#D93025',
        borderColor: '#D93025',
        pointRadius: 4,
        pointHoverRadius: 5,
        fill: false,
        data: numberOfOrdersChartData.baseData,
        labels: numberOfOrdersChartData.baseLabels,
        yAxisID: 'y',
    });

    return datasets;
});

const turnoverDatasets = computed(() => {
    let chartData = options.value.byDay?.active
        ? globalData.value.turnoverChartDataByDay
        : options.value.byWeek?.active
          ? globalData.value.turnoverChartDataByWeek
          : globalData.value.turnoverChartDataByMonth;

    return [
        {
            group: 'CA TTC',
            label: 'CA TTC',
            backgroundColor: '#4d65b9',
            borderColor: '#4d65b9',
            pointRadius: 4,
            pointHoverRadius: 5,
            fill: false,
            data: chartData.baseData,
            labels: chartData.baseLabels,
            yAxisID: 'y',
        },
    ];
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #before-table>
            <div class="flex flex-col gap-4">
                <div class="flex justify-end">
                    <DateRangePicker :callback="updateData" can-select-today />
                </div>

                <CardGrid>
                    <MetricCard
                        title="Emails envoyés"
                        subtitle="Nombre d'emails envoyés"
                        :value="globalData.emailsSent"
                        :with-decimals="false"
                        :icon="EnvelopeOpenIcon"
                    />

                    <MetricCard
                        title="Taux d'ouverture"
                        subtitle="Ratio ouvertures sur emails envoyés"
                        :value="globalData.openRate"
                        type="percentage"
                        :with-decimals="false"
                        :icon="EyeIcon"
                    />

                    <MetricCard
                        title="Taux de clics"
                        subtitle="Ratio clics sur emails envoyés"
                        :value="globalData.clickThroughRate"
                        type="percentage"
                        :with-decimals="false"
                        :icon="MouseLeftClickIcon"
                    />

                    <MetricCard
                        title="Taux de désabonnement"
                        subtitle="Ratio désinscriptions sur emails envoyés"
                        :value="globalData.unsubscriptionRate"
                        type="percentage"
                        :icon="ProhibitIcon"
                    />
                </CardGrid>

                <CardGrid>
                    <MetricCard
                        title="Commandes"
                        subtitle="Commandes liées aux relances"
                        :value="globalData.orders"
                        :suffix-value="'(' + 'total' + ')'"
                        :compare-data="[
                            {
                                value: globalData.numberOfOrdersFromOpenedEmail,
                                suffix: ' (' + 'après ouverture' + ')',
                            },
                            {
                                value: globalData.numberOfOrdersFromClickedEmail,
                                suffix: ' (' + 'après clic' + ')',
                            },
                        ]"
                        :with-decimals="false"
                        :icon="ShoppingCartSimpleIcon"
                        :compare="false"
                    />

                    <MetricCard
                        title="CA TTC"
                        subtitle="CA TTC lié aux relances"
                        :value="globalData.revenue"
                        :suffix-value="'(' + 'total' + ')'"
                        :compare-data="[
                            {
                                value: globalData.revenueFromOpenedEmail,
                                suffix: ' (' + 'après ouverture' + ')',
                            },
                            {
                                value: globalData.revenueFromClickedEmail,
                                suffix: ' (' + 'après clic' + ')',
                            },
                        ]"
                        type="price"
                        :icon="CoinsIcon"
                        :compare="false"
                        :with-decimals="false"
                    />

                    <MetricCard
                        title="Panier moyen"
                        subtitle="Panier moyen lié aux relances"
                        :value="globalData.averageBasket"
                        :suffix-value="'(' + 'total' + ')'"
                        :compare-data="[
                            {
                                value: globalData.averageBasketFromOpenedEmail,
                                suffix: ' (' + 'après ouverture' + ')',
                            },
                            {
                                value: globalData.averageBasketFromClickedEmail,
                                suffix: ' (' + 'après clic' + ')',
                            },
                        ]"
                        type="price"
                        :icon="BasketIcon"
                        :compare="false"
                        :with-decimals="false"
                    />
                </CardGrid>
            </div>
        </template>

        <template #after-table>
            <LineChart
                :datasets="emailsSentAndNumberOfOrdersDatasets"
                title="Emails envoyés et commandes liées aux relances"
                :options="options"
                :with-decimals-axis1="false"
            />

            <LineChart
                :datasets="turnoverDatasets"
                title="CA TTC lié aux relances (après ouverture)"
                :options="options"
                type-axis1="price"
                :with-decimals-axis1="false"
            />
        </template>
    </DataTableLayout>
</template>
